import {
  Text,
  Item,
  Field,
  LinkField,
  withDatasourceCheck,
  getFieldValue,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import { useI18n } from 'next-localization';
import React from 'react';

import { ButtonVariant } from 'components/helpers/Button/Button';
import Icon, { type IconVariant } from 'components/helpers/Icon';
import Link, { LinkVariant } from 'components/helpers/Link/Link';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import type { RecordOfFields } from 'models/fields';

import styles from './SignInBanner.module.scss';

type SignInBannerFields = {
  linkOne: LinkField;
  linkOneIcon: Item;
  linkOneText: Field<string>;
  linkTwo: LinkField;
  linkTwoIcon: Item;
  linkTwoText: Field<string>;
  portalDestinationLink: LinkField;
  signInTitle: Field<string>;
  username: Field<string>;
  usernameErrorText: Field<string>;
};

type SignInBannerProps = ComponentProps & {
  fields: SignInBannerFields;
  rendering: { uid: string };
};

const cx = classNames.bind(styles);

const SignInBanner = (props: SignInBannerProps): JSX.Element => {
  const { className, fields, rendering } = props;
  const { t } = useI18n();

  const [userNameValue, setuserNameValue] = React.useState<string>('');
  const [showError, setShowError] = React.useState<boolean>(false);
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing } = sitecoreContext;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const linkOneIconField = fields.linkOneIcon?.fields as RecordOfFields;
  const linkOneIconValue = getFieldValue<IconVariant>(linkOneIconField, 'Value') || 'carat';

  const linkOneModeValue = LinkVariant.Standalone;

  const linkTwoIconField = fields.linkTwoIcon?.fields as RecordOfFields;
  const linkTwoIconValue = getFieldValue<IconVariant>(linkTwoIconField, 'Value') || 'carat';

  const linkTwoModeValue = LinkVariant.Standalone;

  const usernameHref = `${fields?.portalDestinationLink.value.href}${userNameValue}`;

  const LinkOne = (): JSX.Element | null => {
    if ((fields.linkOne.value.href === undefined || fields.linkOne.value.href) === '')
      return <Text field={fields?.linkOneText} encode={pageEditing} />;

    return (
      <div className={cx('link-one')}>
        <Link
          field={fields?.linkOne}
          icon={linkOneIconValue}
          variant={linkOneModeValue}
          showLinkTextWithChildrenPresent={false}
        >
          <Text field={fields?.linkOneText} encode={pageEditing} />
        </Link>
      </div>
    );
  };

  const LinkTwo = (): JSX.Element | null => {
    if (fields.linkTwo.value.href === undefined || fields.linkTwo.value.href === '')
      return <Text field={fields?.linkTwoText} encode={pageEditing} />;

    return (
      <div className={cx('link-two')}>
        <Link
          field={fields?.linkTwo}
          icon={linkTwoIconValue}
          variant={linkTwoModeValue}
          showLinkTextWithChildrenPresent={false}
        >
          <Text field={fields?.linkTwoText} encode={pageEditing} />
        </Link>
      </div>
    );
  };

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (!userNameValue) {
      e.preventDefault();
      setShowError(true);
      inputRef?.current?.focus();
    }
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;

    setShowError(!value);
    setuserNameValue(value);
  };

  return (
    <>
      <div className={cx('sign-in-container', 'component-container-edge', className)}>
        <div className={cx('sign-in')}>
          <Icon className={cx('person-icon')} variant="person" />
          <label htmlFor={rendering?.uid}>
            <Text field={fields?.signInTitle} encode={pageEditing} />
          </label>
          <div className={cx('input-with-error')}>
            <input
              ref={inputRef}
              onInput={handleChange}
              autoComplete="username"
              className={cx({ 'input-box-error': showError, 'form-control': true })}
              value={userNameValue}
              type="text"
              id={rendering?.uid}
            />
            {showError && (
              <div className={cx('error-msg')} role="alert">
                <Icon variant="error" />
                <Text tag="span" field={fields?.usernameErrorText} encode={pageEditing} />
              </div>
            )}
          </div>
          <Link
            onClick={handleClick}
            field={{
              ...fields?.portalDestinationLink,
              value: { ...fields?.portalDestinationLink.value, href: usernameHref },
            }}
            target={fields?.portalDestinationLink?.value.target}
            showLinkTextWithChildrenPresent={false}
            variant={ButtonVariant.Primary}
            className={cx('submit-link')}
          >
            {t('SignIn-BannerButtonText')}
          </Link>
        </div>
        <LinkOne />
        <LinkTwo />
      </div>
    </>
  );
};

export default compose<SignInBannerProps>(withDatasourceCheck(), withStyles())(SignInBanner);
